import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import {
  PAYMENT_PLAN_EXPLANATIONS,
  PAYMENT_PLAN_FUNDS_MAX,
  can,
  formatCuit,
  type CompanyPersonView,
  type PaymentPlanImportPreview,
  type PaymentPlanInstallmentView,
  type PaymentPlanView,
} from "@devengo/shared";

import { FilePicker } from "../components/file-picker";
import { Dialog } from "../components/period-picker";
import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  Notice,
  PageTitle,
  numClass,
  tableClass,
  tdClass,
  thClass,
} from "../components/ui";
import { api } from "../lib/api";
import { formatCents, formatDate, formatDateTime, formatDecimal } from "../lib/format";
import { useCurrentUser } from "../lib/session";

type Tab = "planes" | "detalle" | "fondos" | "historial";

export interface PaymentPlansSearch {
  tab?: Tab;
  /** The plan in «Cuotas y detalle». */
  plan?: string;
  /** The plans of the consolidated report, comma-separated ids. */
  planes?: string;
}

interface FundsView {
  issuedOn: string;
  plans: { label: string; cuit: string; planNumber: string }[];
  funds: {
    months: {
      month: string;
      dates: string[];
      perPlan: { numbers: number[]; totalCents: number }[];
      totalCents: number;
    }[];
    bands: { from: string; to: string; totalCents: number; reduced: boolean }[];
    perPlanTotals: number[];
    totalCents: number;
    reductions: { from: string; totalCents: number; reductionCents: number }[];
  };
}

interface ReportSummary {
  id: string;
  kind: "individual" | "fondos";
  title: string;
  createdAt: string;
}

const MONTHS = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];
const monthName = (month: string) =>
  `${MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
const pesos = (decimal: string) => `$ ${formatDecimal(decimal)}`;
const cuotaLabel = (n: number) => (n === 0 ? "Pago a cuenta" : String(n));
const linkClass =
  "inline-flex items-center rounded-md border border-brand-line bg-white px-3 py-1.5 text-sm font-medium hover:bg-brand-tint";

/** «12 cuotas · 1 a 12», «4 de 6 · 3, 4, 5, 6 + pago a cuenta». */
function ticked(plan: PaymentPlanView): string {
  const cuotas = plan.installments.filter((i) => i.number > 0);
  const on = cuotas.filter((i) => i.included).map((i) => i.number);
  const payment = plan.installments.find((i) => i.number === 0);
  const count =
    on.length === cuotas.length ? `${on.length} cuotas` : `${on.length} de ${cuotas.length}`;
  const list = on.length === 0 ? "ninguna" : on.join(", ");
  return `${count} · ${list}${payment?.included ? " + pago a cuenta" : ""}`;
}

/**
 * The tab for an issued PDF opens on the click itself: one opened after the answer arrives is a
 * popup browsers block. It gets the PDF when the report exists (the Historial has it too).
 */
const openTab = () => window.open("", "_blank");
function showPdf(tab: Window | null, reportId: string) {
  const url = `/api/payment-plan-reports/${reportId}.pdf`;
  if (tab) tab.location.href = url;
  else window.open(url, "_blank");
}

function useRefresh(companyId: string) {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ["payment-plans", companyId] });
    await client.invalidateQueries({ queryKey: ["company-people", companyId] });
    await client.invalidateQueries({ queryKey: ["payment-plan-funds", companyId] });
    await client.invalidateQueries({ queryKey: ["payment-plan-reports", companyId] });
  };
}

/**
 * Planes de pago of «Mis Facilidades» (plan 025), with the tabs of Maxi's mockup: the plans of
 * the company and its partners, one plan's cuotas, «fondos por mes» and the reports issued.
 */
export function PaymentPlansPage({
  companyId,
  search,
}: {
  companyId: string;
  search: PaymentPlansSearch;
}) {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const reviewer = can(user.role, "managePaymentPlans");
  const tab = search.tab ?? "planes";
  const [importing, setImporting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const plans = useQuery({
    queryKey: ["payment-plans", companyId],
    queryFn: () => api.get<PaymentPlanView[]>(`/api/companies/${companyId}/payment-plans`),
  });
  const active = (plans.data ?? []).filter((p) => !p.archived);
  const chosenIds = (search.planes ?? "")
    .split(",")
    .filter((id) => active.some((p) => p.id === id));
  const consolidated = search.planes !== undefined ? chosenIds : active.map((p) => p.id);

  const go = (patch: Partial<PaymentPlansSearch>) =>
    void navigate({
      to: "/empresas/$companyId/planes-de-pago",
      params: { companyId },
      search: Object.fromEntries(
        Object.entries({ ...search, ...patch }).filter(([, v]) => v !== undefined && v !== ""),
      ) as PaymentPlansSearch,
    });

  return (
    <>
      <PageTitle
        subtitle="Consultá las cuotas y prepará informes de los planes de Mis Facilidades de la empresa y sus socios."
        actions={<Button onClick={() => setImporting(true)}>+ Importar PDF</Button>}
      >
        Planes de pago
      </PageTitle>
      <div className="flex flex-col gap-4">
        {notice ? <Notice tone="success">{notice}</Notice> : null}
        <ErrorBox error={plans.error} />
        <Tabs value={tab} onChange={(t) => go({ tab: t })} />
        {tab === "planes" ? (
          <PlansTab
            companyId={companyId}
            plans={plans.data ?? []}
            loading={plans.isLoading}
            consolidated={consolidated}
            reviewer={reviewer}
            onConsolidated={(ids) => go({ planes: ids.join(",") || "-" })}
            onDetail={(id) => go({ tab: "detalle", plan: id })}
            onFunds={() => go({ tab: "fondos" })}
          />
        ) : null}
        {tab === "detalle" ? (
          <DetailTab
            companyId={companyId}
            plans={plans.data ?? []}
            planId={search.plan ?? active[0]?.id ?? plans.data?.[0]?.id}
            reviewer={reviewer}
            onPlan={(id) => go({ plan: id })}
            onIssued={() => go({ tab: "historial" })}
          />
        ) : null}
        {tab === "fondos" ? (
          <FundsTab
            companyId={companyId}
            plans={active}
            consolidated={consolidated}
            onChangePlans={() => go({ tab: "planes" })}
            onIssued={() => go({ tab: "historial" })}
          />
        ) : null}
        {tab === "historial" ? <HistoryTab companyId={companyId} /> : null}
      </div>
      {importing ? (
        <ImportDialog
          companyId={companyId}
          onClose={() => setImporting(false)}
          onSaved={(plan, unchanged) => {
            setImporting(false);
            setNotice(
              unchanged
                ? `El plan ${plan.planNumber} ya estaba cargado con el mismo contenido: no cambió nada.`
                : `Se guardó el plan ${plan.planNumber} de ${plan.holder.name} (versión ${plan.version}).`,
            );
            go({ tab: "detalle", plan: plan.id });
          }}
          onOpen={(planId) => {
            setImporting(false);
            setNotice("Ese plan ya estaba cargado con el mismo contenido: no cambió nada.");
            go({ tab: "detalle", plan: planId });
          }}
        />
      ) : null}
    </>
  );
}

function Tabs({ value, onChange }: { value: Tab; onChange: (tab: Tab) => void }) {
  const tabs: { id: Tab; label: string }[] = [
    { id: "planes", label: "Planes guardados" },
    { id: "detalle", label: "Cuotas y detalle" },
    { id: "fondos", label: "Fondos por mes" },
    { id: "historial", label: "Historial" },
  ];
  return (
    <div role="tablist" className="flex gap-1 border-b border-brand-line">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={value === t.id}
          className={`-mb-px border-b-2 px-3 py-2 text-sm ${
            value === t.id
              ? "border-brand-primary font-semibold text-brand-primary"
              : "border-transparent text-brand-muted hover:text-brand-text"
          }`}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── Planes guardados ───────────────────────────────────────────────────────────────────────

function PlansTab({
  companyId,
  plans,
  loading,
  consolidated,
  reviewer,
  onConsolidated,
  onDetail,
  onFunds,
}: {
  companyId: string;
  plans: PaymentPlanView[];
  loading: boolean;
  consolidated: string[];
  reviewer: boolean;
  onConsolidated: (ids: string[]) => void;
  onDetail: (id: string) => void;
  onFunds: () => void;
}) {
  const [showArchived, setShowArchived] = useState(false);
  const shown = plans.filter((p) => showArchived || !p.archived);
  const chosen = new Set(consolidated);
  const toggle = (id: string, on: boolean) =>
    onConsolidated(on ? [...consolidated, id] : consolidated.filter((x) => x !== id));
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="flex flex-col gap-4 lg:col-span-2">
        <Card
          title="Planes de la empresa y sus socios"
          actions={
            plans.some((p) => p.archived) ? (
              <label className="flex items-center gap-1.5 text-xs text-brand-muted">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
                Mostrar archivados
              </label>
            ) : null
          }
        >
          {loading ? <p className="text-sm text-brand-muted">Cargando…</p> : null}
          {!loading && shown.length === 0 ? (
            <p className="text-sm text-brand-muted">
              Todavía no hay planes cargados. Importá el PDF que baja Mis Facilidades de ARCA.
            </p>
          ) : null}
          {shown.length > 0 ? (
            <table className={tableClass}>
              <thead>
                <tr>
                  <th className={`${thClass} w-8`} />
                  <th className={thClass}>Titular / plan</th>
                  <th className={thClass}>Cuotas a informar</th>
                  <th className={`${thClass} text-right`}>1.er vencimiento</th>
                  <th className={thClass} />
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => {
                  const next = p.installments.find((i) => i.included && i.number > 0);
                  return (
                    <tr key={p.id} className={p.archived ? "text-brand-muted" : ""}>
                      <td className={tdClass}>
                        <input
                          type="checkbox"
                          aria-label={`Incluir ${p.planNumber} en fondos por mes`}
                          disabled={Boolean(p.archived)}
                          checked={!p.archived && chosen.has(p.id)}
                          onChange={(e) => toggle(p.id, e.target.checked)}
                        />
                      </td>
                      <td className={tdClass}>
                        <div className="font-medium">
                          {p.holder.name}{" "}
                          <Badge tone={p.holder.kind === "empresa" ? "neutral" : "brand"}>
                            {p.holder.kind === "empresa" ? "Empresa" : "Socio"}
                          </Badge>
                          {p.archived ? <Badge tone="warning">Archivado</Badge> : null}
                        </div>
                        <div className="tabular text-xs text-brand-muted">
                          {p.planNumber} · CUIT {formatCuit(p.holder.cuit)} · versión {p.version}
                        </div>
                      </td>
                      <td className={`${tdClass} text-xs`}>{ticked(p)}</td>
                      <td className={`${tdClass} ${numClass}`}>
                        {next ? (
                          <>
                            <div className="font-medium">{pesos(next.first.total)}</div>
                            <div className="text-xs text-brand-muted">
                              cuota {next.number} · {formatDate(next.first.dueOn)}
                            </div>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className={`${tdClass} text-right`}>
                        <Button variant="secondary" onClick={() => onDetail(p.id)}>
                          Ver detalle
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
          {shown.length > 0 ? (
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-brand-muted">
                {consolidated.length} plan{consolidated.length === 1 ? "" : "es"} elegido
                {consolidated.length === 1 ? "" : "s"} para fondos por mes
                {consolidated.length > PAYMENT_PLAN_FUNDS_MAX
                  ? ` (hasta ${PAYMENT_PLAN_FUNDS_MAX} por informe)`
                  : ""}
              </span>
              <Button
                disabled={consolidated.length === 0 || consolidated.length > PAYMENT_PLAN_FUNDS_MAX}
                onClick={onFunds}
              >
                Ver fondos por mes →
              </Button>
            </div>
          ) : null}
        </Card>
      </div>
      <div className="flex flex-col gap-4">
        <NextMonth companyId={companyId} consolidated={consolidated} />
        <PeopleCard companyId={companyId} reviewer={reviewer} />
      </div>
    </div>
  );
}

function useFunds(companyId: string, planIds: string[]) {
  return useQuery({
    queryKey: ["payment-plan-funds", companyId, planIds.join(",")],
    queryFn: () =>
      api.get<FundsView>(
        `/api/companies/${companyId}/payment-plans/funds?planIds=${planIds.join(",")}`,
      ),
    enabled: planIds.length > 0 && planIds.length <= PAYMENT_PLAN_FUNDS_MAX,
    retry: false,
  });
}

/** What the client has to have next: the first month from today with ticked cuotas. */
function NextMonth({ companyId, consolidated }: { companyId: string; consolidated: string[] }) {
  const funds = useFunds(companyId, consolidated);
  const thisMonth = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Argentina/Buenos_Aires",
  })
    .format(new Date())
    .slice(0, 7);
  const next = funds.data?.funds.months.find((m) => m.month >= thisMonth);
  return (
    <Card title="Fondos del próximo mes">
      {next ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <div className="text-xs font-semibold uppercase text-red-800">
            A disponer al {formatDate(next.dates[0] as string)}
          </div>
          <div className="text-2xl font-bold text-red-700">{formatCents(next.totalCents)}</div>
          <div className="text-xs text-red-800">
            Según los primeros vencimientos tildados de {consolidated.length} plan
            {consolidated.length === 1 ? "" : "es"}.
          </div>
        </div>
      ) : (
        <p className="text-sm text-brand-muted">
          {consolidated.length === 0
            ? "Elegí planes para ver cuánto hay que tener el próximo mes."
            : "No hay cuotas tildadas desde este mes en los planes elegidos."}
        </p>
      )}
    </Card>
  );
}

function PeopleCard({ companyId, reviewer }: { companyId: string; reviewer: boolean }) {
  const refresh = useRefresh(companyId);
  const people = useQuery({
    queryKey: ["company-people", companyId],
    queryFn: () => api.get<CompanyPersonView[]>(`/api/companies/${companyId}/people`),
  });
  const [cuit, setCuit] = useState("");
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () => api.post(`/api/companies/${companyId}/people`, { cuit, name }),
    onSuccess: async () => {
      setCuit("");
      setName("");
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/company-people/${id}`),
    onSuccess: refresh,
  });
  return (
    <Card title="Personas asociadas">
      <p className="mb-2 text-xs text-brand-muted">
        Socios u otras personas cuyos planes se informan dentro de esta empresa.
      </p>
      <ErrorBox error={people.error ?? add.error ?? remove.error} />
      {(people.data ?? []).length === 0 ? (
        <p className="text-sm text-brand-muted">Ninguna todavía.</p>
      ) : (
        <ul className="mb-3 flex flex-col gap-1 text-sm">
          {(people.data ?? []).map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2">
              <span>
                {p.name}{" "}
                <span className="tabular text-xs text-brand-muted">{formatCuit(p.cuit)}</span>
                {p.plans > 0 ? (
                  <span className="text-xs text-brand-muted">
                    {" "}
                    · {p.plans} plan{p.plans === 1 ? "" : "es"}
                  </span>
                ) : null}
              </span>
              {reviewer && p.plans === 0 ? (
                <button
                  type="button"
                  className="text-xs text-red-700 hover:underline"
                  onClick={() => remove.mutate(p.id)}
                >
                  Quitar
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {reviewer ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <Field label="CUIT">
            <Input
              value={cuit}
              onChange={(e) => setCuit(e.target.value)}
              placeholder="20-12345678-9"
            />
          </Field>
          <Field label="Nombre">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Button type="submit" variant="secondary" disabled={!cuit || !name || add.isPending}>
            Asociar persona
          </Button>
        </form>
      ) : (
        <p className="text-xs text-brand-muted">
          Asociar personas lo hacen el Contador o el Dueño. Al importar el plan de un socio nuevo,
          también.
        </p>
      )}
    </Card>
  );
}

// ─── Cuotas y detalle ───────────────────────────────────────────────────────────────────────

function DetailTab({
  companyId,
  plans,
  planId,
  reviewer,
  onPlan,
  onIssued,
}: {
  companyId: string;
  plans: PaymentPlanView[];
  planId: string | undefined;
  reviewer: boolean;
  onPlan: (id: string) => void;
  onIssued: () => void;
}) {
  const plan = plans.find((p) => p.id === planId);
  const refresh = useRefresh(companyId);
  const [included, setIncluded] = useState<Set<number>>(new Set());
  const [archiving, setArchiving] = useState(false);
  // What is ticked follows the saved plan: on another plan always; on a newer save of the same
  // one only while nothing is being edited here (Codex 025: a refetch must not drop edits).
  const saved = (p: PaymentPlanView | undefined) =>
    new Set(p?.installments.filter((i) => i.included).map((i) => i.number));
  const [baseline, setBaseline] = useState<{ id: string | undefined; ticks: Set<number> }>({
    id: undefined,
    ticks: new Set(),
  });
  useEffect(() => {
    const ticks = saved(plan);
    const sameAsBaseline =
      included.size === baseline.ticks.size && [...included].every((n) => baseline.ticks.has(n));
    if (plan?.id !== baseline.id || sameAsBaseline) setIncluded(ticks);
    setBaseline({ id: plan?.id, ticks });
    // Only a new plan or a new save moves the baseline.
  }, [plan?.id, plan?.updatedAt]);
  const save = useMutation({
    mutationFn: ({ id, sent }: { id: string; sent: Set<number> }) =>
      api.put(`/api/payment-plans/${id}/selection`, { included: [...sent] }),
    // What was sent is the new baseline, for the plan it was sent for: edits made after it
    // survive the refetch, and another plan chosen meanwhile keeps its own (Codex 025).
    onSuccess: async (_plan, { id, sent }) => {
      setBaseline((current) => (current.id === id ? { id, ticks: sent } : current));
      await refresh();
    },
  });
  const issue = useMutation({
    mutationFn: ({ both }: { both: boolean; tab: Window | null }) =>
      api.post<ReportSummary>(`/api/payment-plans/${planId}/reports`, { both }),
    onSuccess: async (report, { tab }) => {
      showPdf(tab, report.id);
      await refresh();
      onIssued();
    },
    onError: (_error, { tab }) => tab?.close(),
  });
  if (!plan) {
    return (
      <p className="text-sm text-brand-muted">
        {plans.length === 0 ? "Todavía no hay planes cargados." : "Elegí un plan."}
      </p>
    );
  }
  const dirty = plan.installments.some((i) => i.included !== included.has(i.number));
  const cuotas = plan.installments.filter((i) => i.number > 0);
  const firstOn = cuotas.find((i) => included.has(i.number));
  // Only the ticked cuotas need their second due date (the API checks the same).
  const hasSecond = cuotas.every((i) => !included.has(i.number) || i.second);
  const toggle = (n: number, on: boolean) =>
    setIncluded((current) => {
      const next = new Set(current);
      if (on) next.add(n);
      else next.delete(n);
      return next;
    });
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Plan a consultar">
          <select
            className="rounded-md border border-brand-line bg-white px-2.5 py-1.5 text-sm"
            value={plan.id}
            // Not while a save is on its way: its answer belongs to this plan (Codex 025).
            disabled={save.isPending}
            onChange={(e) => onPlan(e.target.value)}
          >
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.holder.name} · {p.planNumber}
                {p.archived ? " (archivado)" : ""}
              </option>
            ))}
          </select>
        </Field>
        <div className="flex-1" />
        <a className={linkClass} href={`/api/payment-plans/${plan.id}/original`} download>
          PDF original de ARCA
        </a>
        {reviewer ? (
          <Button variant="secondary" onClick={() => setArchiving(true)}>
            {plan.archived ? "Reactivar" : "Archivar"}
          </Button>
        ) : null}
      </div>
      {plan.archived ? (
        <Notice tone="warning">
          Archivado el {formatDateTime(plan.archived.at)}: {plan.archived.reason}. No se informa.
        </Notice>
      ) : null}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Card>
          <div className="text-xs text-brand-muted">Cuotas a informar</div>
          <div className="text-2xl font-bold text-brand-primary">
            {cuotas.filter((i) => included.has(i.number)).length} de {cuotas.length}
          </div>
          <div className="text-xs text-brand-muted">
            {included.has(0) ? "Con el pago a cuenta" : "Tildá solo lo que va en el informe"}
          </div>
        </Card>
        <Card>
          <div className="text-xs text-brand-muted">Primer vencimiento</div>
          <div className="text-2xl font-bold text-brand-primary">
            {firstOn ? pesos(firstOn.first.total) : "—"}
          </div>
          <div className="text-xs text-brand-muted">Primera cuota tildada</div>
        </Card>
        <Card>
          <div className="text-xs text-brand-muted">Segundo vencimiento</div>
          <div className="text-2xl font-bold text-brand-primary">
            {firstOn?.second ? pesos(firstOn.second.total) : "—"}
          </div>
          <div className="text-xs text-brand-muted">Alternativa del primero: no se suman</div>
        </Card>
      </div>

      <Card
        title={`${plan.holder.name} · ${plan.planNumber}`}
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={Boolean(plan.archived)}
              onClick={() => setIncluded(new Set(plan.installments.map((i) => i.number)))}
            >
              Todas
            </Button>
            <Button
              variant="secondary"
              disabled={Boolean(plan.archived)}
              onClick={() => setIncluded(new Set())}
            >
              Ninguna
            </Button>
          </div>
        }
      >
        <InstallmentsTable
          rows={plan.installments}
          included={included}
          disabled={Boolean(plan.archived)}
          onToggle={toggle}
        />
        <ErrorBox error={save.error ?? issue.error} />
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          {dirty ? (
            <span className="text-sm text-amber-800">Hay cambios sin guardar en lo tildado.</span>
          ) : null}
          <Button
            disabled={!dirty || save.isPending}
            onClick={() => plan && save.mutate({ id: plan.id, sent: new Set(included) })}
          >
            Guardar cuotas a informar
          </Button>
          <Button
            variant="secondary"
            disabled={dirty || Boolean(plan.archived) || included.size === 0 || issue.isPending}
            onClick={() => issue.mutate({ both: false, tab: openTab() })}
          >
            PDF · primer vencimiento
          </Button>
          <Button
            variant="secondary"
            disabled={
              dirty ||
              !hasSecond ||
              Boolean(plan.archived) ||
              included.size === 0 ||
              issue.isPending
            }
            onClick={() => issue.mutate({ both: true, tab: openTab() })}
          >
            PDF · ambos vencimientos
          </Button>
        </div>
      </Card>
      <Explanations />
      {archiving ? (
        <ArchiveDialog plan={plan} onClose={() => setArchiving(false)} onDone={refresh} />
      ) : null}
    </div>
  );
}

function InstallmentsTable({
  rows,
  included,
  disabled,
  onToggle,
}: {
  rows: PaymentPlanInstallmentView[];
  included: Set<number>;
  disabled: boolean;
  onToggle: (n: number, on: boolean) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className={tableClass}>
        <thead>
          <tr>
            <th className={`${thClass} w-8`} />
            <th className={thClass}>Cuota</th>
            <th className={`${thClass} text-right`}>Capital</th>
            <th className={`${thClass} text-right`}>Int. financiero</th>
            <th className={thClass}>1.er vencimiento</th>
            <th className={`${thClass} text-right`}>Total</th>
            <th className={thClass}>2.º vencimiento</th>
            <th className={`${thClass} text-right`}>Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((i) => {
            const on = included.has(i.number);
            return (
              <tr key={i.number} className={on ? "" : "text-brand-muted"}>
                <td className={tdClass}>
                  <input
                    type="checkbox"
                    aria-label={`Informar ${cuotaLabel(i.number).toLowerCase()}`}
                    disabled={disabled}
                    checked={on}
                    onChange={(e) => onToggle(i.number, e.target.checked)}
                  />
                </td>
                <td className={tdClass}>
                  {i.number === 0 ? "Pago a cuenta" : `Cuota ${i.number}`}
                </td>
                <td className={`${tdClass} ${numClass}`}>{pesos(i.capital)}</td>
                <td className={`${tdClass} ${numClass}`}>{pesos(i.first.interest)}</td>
                <td className={`${tdClass} tabular`}>{formatDate(i.first.dueOn)}</td>
                <td className={`${tdClass} ${numClass} font-semibold ${on ? "text-red-700" : ""}`}>
                  {pesos(i.first.total)}
                </td>
                <td className={`${tdClass} tabular`}>
                  {i.second ? formatDate(i.second.dueOn) : "—"}
                </td>
                <td className={`${tdClass} ${numClass}`}>
                  {i.second ? pesos(i.second.total) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Explanations() {
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
      <div className="mb-1 font-semibold">Cómo leer el plan</div>
      <ul className="flex flex-col gap-0.5">
        {PAYMENT_PLAN_EXPLANATIONS.map((e) => (
          <li key={e.title}>
            <b>{e.title}:</b> {e.body}
          </li>
        ))}
        <li>Destildar una cuota la saca de los informes: no la marca como pagada.</li>
      </ul>
    </div>
  );
}

function ArchiveDialog({
  plan,
  onClose,
  onDone,
}: {
  plan: PaymentPlanView;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const archiving = !plan.archived;
  const change = useMutation({
    mutationFn: () =>
      api.post(`/api/payment-plans/${plan.id}/${archiving ? "archive" : "unarchive"}`, { reason }),
    onSuccess: async () => {
      await onDone();
      onClose();
    },
  });
  return (
    <Dialog title={archiving ? "Archivar plan" : "Reactivar plan"} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm">
          {archiving
            ? `El plan ${plan.planNumber} de ${plan.holder.name} deja de aparecer en los informes. No se borra: se puede reactivar.`
            : `El plan ${plan.planNumber} vuelve a los informes.`}
        </p>
        <Field label="Motivo (obligatorio)">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              archiving ? "Ej.: se cargó en la empresa equivocada" : "Ej.: se archivó por error"
            }
          />
        </Field>
        <ErrorBox error={change.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant={archiving ? "danger" : "primary"}
            disabled={reason.trim().length < 5 || change.isPending}
            onClick={() => change.mutate()}
          >
            {archiving ? "Archivar" : "Reactivar"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ─── Fondos por mes ─────────────────────────────────────────────────────────────────────────

function FundsTab({
  companyId,
  plans,
  consolidated,
  onChangePlans,
  onIssued,
}: {
  companyId: string;
  plans: PaymentPlanView[];
  consolidated: string[];
  onChangePlans: () => void;
  onIssued: () => void;
}) {
  const refresh = useRefresh(companyId);
  const funds = useFunds(companyId, consolidated);
  const chosen = useMemo(
    () => consolidated.flatMap((id) => plans.filter((p) => p.id === id)),
    [consolidated, plans],
  );
  // Omitting a month unticks its cuotas in every chosen plan, all at once (Mathi: only what is
  // ticked goes).
  const omit = useMutation({
    mutationFn: (month: string) =>
      api.post(`/api/companies/${companyId}/payment-plans/omit-month`, {
        planIds: consolidated,
        month,
      }),
    onSettled: refresh,
  });
  const issue = useMutation({
    mutationFn: (_tab: Window | null) =>
      api.post<ReportSummary>(`/api/companies/${companyId}/payment-plans/funds-reports`, {
        planIds: consolidated,
      }),
    onSuccess: async (report, tab) => {
      showPdf(tab, report.id);
      await refresh();
      onIssued();
    },
    onError: (_error, tab) => tab?.close(),
  });
  if (consolidated.length === 0) {
    return (
      <p className="text-sm text-brand-muted">
        Elegí los planes en «Planes guardados» para ver los fondos por mes.
      </p>
    );
  }
  const view = funds.data;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm">
          {chosen.map((p, i) => (
            <span key={p.id} className="mr-3">
              <b>{i + 1}.</b> {p.holder.name} · {p.planNumber}
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onChangePlans}>
            Cambiar planes
          </Button>
          <Button
            disabled={!view || view.funds.months.length === 0 || issue.isPending}
            onClick={() => issue.mutate(openTab())}
          >
            Emitir PDF
          </Button>
        </div>
      </div>
      <ErrorBox error={funds.error ?? omit.error ?? issue.error} />
      {view && view.funds.months.length === 0 ? (
        <Notice tone="info">No hay cuotas tildadas en los planes elegidos.</Notice>
      ) : null}
      {view && view.funds.bands.length > 0 ? (
        <div className="flex flex-col gap-1">
          {view.funds.bands.map((b) => (
            <div
              key={b.from}
              className={`flex justify-between rounded-md px-3 py-2 font-semibold ${
                b.reduced ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"
              }`}
            >
              <span>
                {monthName(b.from)}
                {b.from !== b.to ? ` a ${monthName(b.to)}` : ""}
              </span>
              <span>{formatCents(b.totalCents)} por mes</span>
            </div>
          ))}
        </div>
      ) : null}
      {view && view.funds.months.length > 0 ? (
        <Card
          title="Fondos a disponer por mes"
          actions={<span className="text-xs text-brand-muted">al primer vencimiento</span>}
        >
          <div className="overflow-x-auto">
            <table className={tableClass}>
              <thead>
                <tr>
                  <th className={thClass}>Vencimiento</th>
                  <th className={thClass}>Cuotas</th>
                  {view.plans.map((p, i) => (
                    <th key={i} className={`${thClass} text-right`}>
                      {i + 1}. {p.label}
                    </th>
                  ))}
                  <th className={`${thClass} text-right`}>Total a disponer</th>
                  <th className={thClass} />
                </tr>
              </thead>
              <tbody>
                {view.funds.months.map((m) => (
                  <tr key={m.month}>
                    <td className={`${tdClass} tabular`}>{m.dates.map(formatDate).join(", ")}</td>
                    <td className={`${tdClass} tabular`}>
                      {m.perPlan
                        .map((c) => (c.numbers.length ? c.numbers.map(cuotaLabel).join(", ") : "-"))
                        .join(" / ")}
                    </td>
                    {m.perPlan.map((c, i) => (
                      <td key={i} className={`${tdClass} ${numClass}`}>
                        {c.totalCents ? formatCents(c.totalCents) : "—"}
                      </td>
                    ))}
                    <td className={`${tdClass} ${numClass} font-semibold text-red-700`}>
                      {formatCents(m.totalCents)}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      <button
                        type="button"
                        className="text-xs text-brand-primary hover:underline"
                        disabled={omit.isPending}
                        onClick={() => omit.mutate(m.month)}
                      >
                        Omitir mes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className={tdClass} colSpan={2}>
                    Total del período
                  </td>
                  {view.funds.perPlanTotals.map((t, i) => (
                    <td key={i} className={`${tdClass} ${numClass}`}>
                      {formatCents(t)}
                    </td>
                  ))}
                  <td className={`${tdClass} ${numClass}`}>{formatCents(view.funds.totalCents)}</td>
                  <td className={tdClass} />
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="mt-2 text-xs text-brand-muted">
            Solo se suman las cuotas tildadas de los planes elegidos. «Omitir mes» destilda las
            cuotas de ese mes en cada plan (por ejemplo, las que ya están pagas); se vuelven a
            tildar en «Cuotas y detalle».
          </p>
        </Card>
      ) : null}
      {view?.funds.reductions.map((r) => (
        <Notice key={r.from} tone="warning">
          <b>Nuevo importe desde el {formatDate(r.from)}:</b> {formatCents(r.totalCents)} por mes
          (baja {formatCents(r.reductionCents)}).
        </Notice>
      ))}
    </div>
  );
}

// ─── Historial ──────────────────────────────────────────────────────────────────────────────

function HistoryTab({ companyId }: { companyId: string }) {
  const history = useQuery({
    queryKey: ["payment-plan-reports", companyId],
    queryFn: () => api.get<ReportSummary[]>(`/api/companies/${companyId}/payment-plan-reports`),
  });
  return (
    <Card title="Informes emitidos">
      <ErrorBox error={history.error} />
      {(history.data ?? []).length === 0 ? (
        <p className="text-sm text-brand-muted">Todavía no se emitieron informes.</p>
      ) : (
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={thClass}>Fecha</th>
              <th className={thClass}>Informe</th>
              <th className={thClass} />
            </tr>
          </thead>
          <tbody>
            {(history.data ?? []).map((r) => (
              <tr key={r.id}>
                <td className={`${tdClass} tabular`}>{formatDateTime(r.createdAt)}</td>
                <td className={tdClass}>{r.title}</td>
                <td className={`${tdClass} text-right`}>
                  <a
                    className="text-sm text-brand-primary hover:underline"
                    href={`/api/payment-plan-reports/${r.id}.pdf`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Ver PDF
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="mt-2 text-xs text-brand-muted">
        Cada informe se vuelve a bajar igual que cuando se emitió, aunque después cambien las cuotas
        tildadas o se importe una versión nueva del plan.
      </p>
    </Card>
  );
}

// ─── Importar ───────────────────────────────────────────────────────────────────────────────

function ImportDialog({
  companyId,
  onClose,
  onSaved,
  onOpen,
}: {
  companyId: string;
  onClose: () => void;
  onSaved: (plan: PaymentPlanView, unchanged: boolean) => void;
  /** The plan the PDF already is, unchanged: shown instead of saved again. */
  onOpen: (planId: string) => void;
}) {
  const refresh = useRefresh(companyId);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PaymentPlanImportPreview | null>(null);
  const read = useMutation({
    mutationFn: () => {
      const form = new FormData();
      if (file) form.append("file", file);
      return api.upload<PaymentPlanImportPreview>(
        `/api/companies/${companyId}/payment-plans/preview`,
        form,
      );
    },
    onSuccess: setPreview,
  });
  const confirm = useMutation({
    mutationFn: () => {
      const form = new FormData();
      if (file) form.append("file", file);
      form.append("fileHash", preview?.fileHash ?? "");
      return api.upload<{ plan: PaymentPlanView; unchanged: boolean }>(
        `/api/companies/${companyId}/payment-plans`,
        form,
      );
    },
    onSuccess: async (result) => {
      await refresh();
      onSaved(result.plan, result.unchanged);
    },
  });
  const cuotas = preview?.plan.installments.filter((i) => i.number > 0) ?? [];
  const payment = preview?.plan.installments.find((i) => i.number === 0);
  return (
    <Dialog title="Importar PDF de Mis Facilidades" onClose={onClose}>
      <div className="flex flex-col gap-3">
        {!preview ? (
          <>
            <p className="text-sm text-brand-muted">
              En ARCA, «Mis Facilidades» → el plan → imprimir o guardar como PDF. Subí el archivo
              tal como se descargó. Puede ser un plan de la empresa o de un socio.
            </p>
            <FilePicker
              file={file}
              onChange={(f) => {
                setFile(f);
                setPreview(null);
              }}
              accept=".pdf"
              label="PDF de Mis Facilidades"
              hint="o arrastralo acá · PDF"
            />
            <ErrorBox error={read.error} />
          </>
        ) : (
          <>
            <div className="rounded-md border border-brand-line p-3 text-sm">
              <div className="font-semibold">{preview.plan.holderName}</div>
              <div className="tabular text-xs text-brand-muted">
                CUIT {formatCuit(preview.plan.holderCuit)} · Plan {preview.plan.planNumber}
                {preview.plan.consolidatedOn
                  ? ` · Consolidación ${formatDate(preview.plan.consolidatedOn)}`
                  : ""}
              </div>
              <div className="mt-2">
                {cuotas.length} cuotas
                {cuotas[0] ? ` de ${pesos(cuotas[0].first.total)} al primer vencimiento` : ""}
                {cuotas[0]
                  ? `, del ${formatDate(cuotas[0].first.dueOn)} al ${formatDate((cuotas.at(-1) ?? cuotas[0]).first.dueOn)}`
                  : ""}
                .
                {payment
                  ? ` Pago a cuenta de ${pesos(payment.first.total)} al ${formatDate(payment.first.dueOn)}.`
                  : ""}
              </div>
              <div className="mt-1 text-xs text-emerald-800">
                Los importes coinciden con los totales que imprime ARCA.
              </div>
            </div>
            {preview.holder.kind === "empresa" ? (
              <Notice tone="info">Es un plan de la empresa.</Notice>
            ) : preview.holder.kind === "persona" ? (
              <Notice tone="info">
                Es un plan de {preview.holder.name}, persona asociada a la empresa.
              </Notice>
            ) : (
              <Notice tone="warning">
                {preview.plan.holderName} no está asociado a esta empresa: al confirmar se asocia
                como persona (lo puede hacer el Contador o el Dueño).
              </Notice>
            )}
            {preview.unchanged ? (
              <Notice tone="success">
                Este plan ya está cargado con el mismo contenido: no hay nada que confirmar.
              </Notice>
            ) : preview.existing ? (
              <Notice tone="warning">
                <div className="font-medium">
                  El plan ya está cargado (versión {preview.existing.version}). Se guarda una
                  versión nueva y se conserva lo tildado:
                </div>
                <ul className="mt-1 list-disc pl-5">
                  {preview.existing.changes.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </Notice>
            ) : null}
            <ErrorBox error={confirm.error} />
          </>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={preview ? () => setPreview(null) : onClose}>
            {preview ? "Elegir otro" : "Cancelar"}
          </Button>
          {!preview ? (
            <Button disabled={!file || read.isPending} onClick={() => read.mutate()}>
              {read.isPending ? "Leyendo…" : "Leer el PDF"}
            </Button>
          ) : preview.unchanged ? (
            <Button onClick={() => preview.existing && onOpen(preview.existing.planId)}>
              Ver el plan
            </Button>
          ) : (
            <Button disabled={confirm.isPending} onClick={() => confirm.mutate()}>
              {preview.existing ? "Guardar versión nueva" : "Guardar plan"}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
